import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { http } from "../../utils/axios";
import { showApiError, showApiSuccess } from "../../utils/toast";
import { Eye, EyeOff } from "lucide-react";

export default function ResetPasswordPage() {
  const logo = new URL(`../../assets/images/logo.png`, import.meta.url);

  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const mobile = location.state?.mobile;

  useEffect(() => {
    if (!mobile) {
      showApiError(null, "Mobile number is required");
      navigate("/forgot-password");
    }
  }, [mobile, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate OTP
    if (!/^[0-9]{6}$/.test(otp)) {
      showApiError(null, "Please enter a valid 6-digit OTP");
      return;
    }

    // Validate password length
    if (newPassword.length < 6) {
      showApiError(null, "Password must be at least 6 characters");
      return;
    }

    // Validate password match
    if (newPassword !== confirmPassword) {
      showApiError(null, "Passwords do not match");
      return;
    }

    setIsSubmitting(true);

    try {
      await http.post("/dashboard/auth/reset-password", {
        mobile,
        otp,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });

      showApiSuccess("Password reset successfully. You can now login with your new password.");
      navigate("/login");
    } catch (err) {
      showApiError(err, "Failed to reset password. Please check your OTP.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      await http.post("/dashboard/auth/forgot-password", { mobile });
      showApiSuccess("OTP resent successfully");
    } catch (err) {
      showApiError(err, "Failed to resend OTP");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow p-6">
        <div className="w-full justify-center flex-col flex items-center">
          <img src={logo} alt="Logo" className="w-48" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">
            Reset Password
          </h1>
          <p className="text-gray-600 text-center mb-6">
            Enter the OTP sent to {mobile}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              OTP
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter 6-digit OTP"
              maxLength={6}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-500" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-500" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-500" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-500" />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 text-white rounded-md px-4 py-2 hover:bg-blue-700 transition-colors disabled:bg-blue-400 flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Resetting Password...
              </>
            ) : (
              "Reset Password"
            )}
          </button>

          <div className="text-center mt-4 space-y-2">
            <button
              type="button"
              onClick={handleResendOtp}
              className="text-blue-600 hover:text-blue-700 text-sm"
            >
              Resend OTP
            </button>
            <div>
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="text-gray-600 hover:text-gray-700 text-sm"
              >
                Back to Login
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
